import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, BadgeCheck } from "lucide-react";
import { MaskLine, Reveal, setSEO } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { EditorialMarquee } from "@/components/Marquee";
import { EnquiryForm } from "@/components/EnquiryForm";
import { TubularHero3D } from "@/components/TubularHero3D";
import { LogoMarquee } from "@/components/LogoMarquee";
import { ParallaxImage } from "@/components/ParallaxImage";
import { IMAGES, PILLARS, TIMELINE, CAPABILITIES, INFRASTRUCTURE } from "@/lib/data";

const Hero = () => (
    <section className="relative min-h-screen overflow-hidden flex items-center" data-testid="home-hero">
        {/* Cinematic 3D precision tubular component — overflows the right edge for depth */}
        <div className="absolute inset-0 lg:left-auto lg:w-[78%] lg:-right-[9%] z-0" data-testid="hero-canvas">
            <TubularHero3D />
        </div>

        {/* Cinematic grade overlays */}
        <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-r from-sf-black via-sf-black/75 to-sf-black/5 lg:via-sf-black/40" />
        <div className="absolute inset-x-0 bottom-0 h-44 z-[1] pointer-events-none bg-gradient-to-t from-sf-black to-transparent" />
        <div className="absolute inset-x-0 top-0 h-28 z-[1] pointer-events-none bg-gradient-to-b from-sf-black/80 to-transparent" />

        {/* HUD engineering annotations */}
        <div className="hidden xl:block absolute right-[7%] top-[24%] z-[2] pointer-events-none" data-testid="hero-hud-process">
            <p className="font-mono text-[10px] tracking-[0.3em] text-sf-green/90 uppercase">Tube Cutting &amp; Bending</p>
            <div className="h-px w-28 bg-sf-green/40 mt-3" />
        </div>
        <div className="hidden xl:block absolute right-[13%] bottom-[19%] z-[2] pointer-events-none text-right" data-testid="hero-hud-tolerance">
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/60 uppercase">Tolerance ± 0.01 mm</p>
            <div className="h-px w-28 bg-white/25 mt-3 ml-auto" />
        </div>

        <div className="sf-container relative z-10 w-full pt-36 pb-28 lg:pt-28">
            <div className="max-w-2xl">
                <Reveal delay={0.1}>
                    <div className="flex items-center gap-4 mb-8">
                        <span className="h-px w-12 bg-sf-green" />
                        <p className="sf-overline" data-testid="hero-overline">ISO 9001:2015 Certified — Est. 2007</p>
                    </div>
                </Reveal>
                <h1 className="font-display font-extrabold leading-[0.92] tracking-tight" data-testid="hero-company-name">
                    <MaskLine delay={0.25} className="text-6xl sm:text-8xl lg:text-9xl">
                        <span className="bg-gradient-to-b from-white via-[#dde3ea] to-[#7d8590] bg-clip-text text-transparent">
                            SUPER FINISHERS
                        </span>
                    </MaskLine>
                    <MaskLine delay={0.5} className="text-3xl sm:text-5xl lg:text-6xl text-white mt-4">
                        Precision That Powers <span className="text-sf-green">Progress.</span>
                    </MaskLine>
                </h1>
                <Reveal delay={0.8}>
                    <p className="text-sf-silver text-base md:text-lg leading-relaxed mt-8 max-w-xl" data-testid="hero-subheading">
                        Advanced manufacturing. Engineered quality. Precision tubular components built for demanding industries.
                    </p>
                </Reveal>
                <Reveal delay={0.95}>
                    <div className="flex flex-wrap gap-4 mt-12">
                        <Link to="/products" data-testid="hero-cta-products" className="sf-btn-primary">
                            Explore Our Products <ArrowUpRight size={16} />
                        </Link>
                        <Link to="/contact" data-testid="hero-cta-contact" className="sf-btn-ghost">
                            Contact Us
                        </Link>
                    </div>
                </Reveal>
                <Reveal delay={1.05}>
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-14 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40" data-testid="hero-spec-strip">
                        <span>Est. 2007</span>
                        <span className="h-1 w-1 bg-sf-green rotate-45" />
                        <span>Precision Tubular Components</span>
                        <span className="h-1 w-1 bg-sf-green rotate-45" />
                        <span>Success Through Quality</span>
                    </div>
                </Reveal>
            </div>
        </div>

        <div className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex items-center gap-3 text-white/40">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
            <ArrowDown size={14} className="animate-bounce" />
        </div>
    </section>
);

const Intro = () => (
    <section className="py-28 md:py-40" data-testid="home-intro">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
                <SectionHeading overline="Who We Are" title="Engineering Excellence Since 2007" />
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
                <Reveal delay={0.15}>
                    <p className="text-white/80 text-base md:text-lg leading-relaxed">
                        Super Finishers was founded by Mr. Rajeev Gupta with a single objective — to serve the industry through
                        high-quality precision tubular components. Today, we are a trusted manufacturing partner for the shock
                        absorber, automotive and cycle industries.
                    </p>
                    <p className="text-sf-silver text-sm md:text-base leading-relaxed mt-6">
                        Focused on innovation, engineering, precision manufacturing, consistent quality and customer satisfaction —
                        with dedicated capabilities in R&D, quality management, business development and production.
                    </p>
                    <Link to="/about" data-testid="intro-about-link" className="inline-flex items-center gap-2 text-sf-green font-mono text-xs tracking-[0.25em] uppercase mt-10 hover:gap-4 transition-[gap] duration-300">
                        Our Story <ArrowUpRight size={14} />
                    </Link>
                </Reveal>
            </div>
        </div>
    </section>
);

const Pillars = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-pillars">
        <div className="sf-container">
            <SectionHeading overline="Our Brand Pillars" title="Built on Three Principles" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-16">
                {PILLARS.map((p, i) => (
                    <Reveal key={p.num} delay={i * 0.12} className="bg-sf-black">
                        <div className="group p-10 md:p-12 h-full hover:bg-sf-surface transition-colors duration-500" data-testid={`pillar-${p.title.toLowerCase()}`}>
                            <p className="font-mono text-sf-green text-sm">{p.num}</p>
                            <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white mt-6 group-hover:text-sf-green transition-colors duration-500">
                                {p.title.toUpperCase()}
                            </h3>
                            <p className="text-sf-silver text-sm leading-relaxed mt-5">{p.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Journey = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-journey">
        <div className="sf-container">
            <SectionHeading overline="Company Journey" title="Two Decades of Momentum" />
            <div className="mt-20 relative">
                <motion.div
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 origin-top"
                />
                {TIMELINE.map((t, i) => (
                    <Reveal key={t.year} delay={i * 0.1}>
                        <div className={`relative flex flex-col md:flex-row gap-6 md:gap-0 mb-16 last:mb-0 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`} data-testid={`timeline-${t.year.toLowerCase()}`}>
                            <div className={`md:w-1/2 pl-12 md:pl-0 ${i % 2 === 1 ? "md:pl-16" : "md:pr-16 md:text-right"}`}>
                                <p className="font-display font-extrabold text-5xl md:text-6xl text-sf-green leading-none">{t.year}</p>
                                <h3 className="font-display font-bold text-xl text-white mt-4">{t.title}</h3>
                                <p className="text-sf-silver text-sm leading-relaxed mt-3 max-w-md md:ml-auto">{t.text}</p>
                            </div>
                            <span className="absolute left-4 md:left-1/2 top-2 -translate-x-1/2 h-3 w-3 bg-sf-green rotate-45" />
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const CapabilitiesPreview = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-capabilities">
        <div className="sf-container">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                <SectionHeading overline="What We Manufacture" title="Manufacturing Capabilities" />
                <Reveal delay={0.2}>
                    <Link to="/capabilities" data-testid="capabilities-view-all" className="sf-btn-ghost shrink-0">
                        View All Capabilities <ArrowUpRight size={16} />
                    </Link>
                </Reveal>
            </div>
            <div className="mt-16 border-t border-white/10">
                {CAPABILITIES.map((c, i) => (
                    <Reveal key={c.num} delay={i * 0.06}>
                        <Link
                            to="/capabilities"
                            data-testid={`capability-row-${c.num}`}
                            className="group grid grid-cols-12 items-center gap-4 py-8 border-b border-white/10 hover:bg-sf-surface transition-colors duration-500 px-2 md:px-6"
                        >
                            <span className="col-span-2 md:col-span-1 font-mono text-sf-green text-sm">{c.num}</span>
                            <h3 className="col-span-10 md:col-span-5 font-display font-bold text-xl md:text-2xl text-white group-hover:text-sf-green transition-colors duration-500">
                                {c.title}
                            </h3>
                            <p className="col-span-10 col-start-3 md:col-span-5 md:col-start-7 text-sf-silver text-sm leading-relaxed">{c.text}</p>
                            <ArrowUpRight className="hidden md:block col-span-1 justify-self-end text-white/30 group-hover:text-sf-green group-hover:rotate-45 transition-[color,transform] duration-500" size={22} />
                        </Link>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const QualityBanner = () => (
    <section className="relative py-32 md:py-44 border-t border-white/10 overflow-hidden" data-testid="home-quality">
        <div className="absolute inset-0 -z-10">
            <img src={IMAGES.components} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-sf-black/60" />
        </div>
        <div className="sf-container text-center">
            <Reveal>
                <p className="inline-flex items-center gap-2 border border-sf-green/40 text-sf-green font-mono text-[11px] tracking-[0.3em] uppercase px-5 py-2.5" data-testid="quality-iso-badge">
                    <BadgeCheck size={14} /> ISO 9001:2015 Certified
                </p>
                <h2 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight text-white mt-10">
                    SUCCESS THROUGH <span className="text-sf-green">QUALITY</span>
                </h2>
                <p className="text-sf-silver text-base md:text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
                    Our motto is our operating system — embedded in every process, every machine, every component.
                </p>
                <Link to="/quality" data-testid="quality-explore-link" className="sf-btn-primary mt-12">
                    Our Quality Standards <ArrowUpRight size={16} />
                </Link>
            </Reveal>
        </div>
    </section>
);

const InfrastructurePreview = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-infrastructure">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
                <ParallaxImage src={IMAGES.factory} alt="Tube fabrication at Super Finishers" testid="infrastructure-image" className="h-[480px]">
                    <div className="absolute bottom-0 left-0 bg-sf-green text-sf-black font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 z-10">
                        Tube Fabrication
                    </div>
                </ParallaxImage>
            </Reveal>
            <div>
                <SectionHeading overline="Our Infrastructure" title="Machines. Methods. Minds." />
                <div className="mt-10 space-y-0 border-t border-white/10">
                    {INFRASTRUCTURE.map((item, i) => (
                        <Reveal key={item.title} delay={i * 0.08}>
                            <div className="py-6 border-b border-white/10 group" data-testid={`infra-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                <h3 className="font-display font-bold text-lg text-white group-hover:text-sf-green transition-colors duration-300">{item.title}</h3>
                                <p className="text-sf-silver text-sm mt-2 leading-relaxed">{item.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Link to="/quality" data-testid="infrastructure-explore-link" className="sf-btn-ghost mt-10">
                    Explore Infrastructure <ArrowUpRight size={16} />
                </Link>
            </div>
        </div>
    </section>
);

const Founder = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-founder">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
                <Reveal>
                    <div className="sf-image-frame group max-w-md" data-testid="founder-image">
                        <img src="/founder.webp" alt="Mr. Rajeev Gupta — Founder and Chairman, Super Finishers" className="w-full aspect-[3/4] object-cover object-top grayscale group-hover:grayscale-0 transition-[filter] duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-sf-black/60 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                            <p className="font-display font-bold text-white text-lg">Rajeev Gupta</p>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-sf-green uppercase mt-1">Founder & Chairman</p>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
                <SectionHeading overline="Leadership" title="Guided by Experience" />
                <Reveal delay={0.15}>
                    <p className="text-white/80 text-base md:text-lg leading-relaxed mt-8">
                        Mr. Rajeev Gupta holds a Bachelor&apos;s degree in Electrical Engineering and brings more than two decades of
                        experience in technical and management positions.
                    </p>
                    <p className="text-sf-silver text-sm md:text-base leading-relaxed mt-6">
                        Under his professional guidance, Super Finishers has developed an efficient operational management system.
                        His mission: to make Super Finishers an industry leader in unmatched quality — integrating innovation
                        into every product we develop.
                    </p>
                    <Link to="/about" data-testid="founder-about-link" className="inline-flex items-center gap-2 text-sf-green font-mono text-xs tracking-[0.25em] uppercase mt-10 hover:gap-4 transition-[gap] duration-300">
                        Meet Our Founder <ArrowUpRight size={14} />
                    </Link>
                </Reveal>
            </div>
        </div>
    </section>
);

const Clients = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="home-clients">
        <div className="sf-container">
            <SectionHeading
                overline="Our Customers"
                title="Our Industries & Clients"
                description="Long-standing partnerships with some of the most demanding names in Indian manufacturing."
            />
        </div>
        <Reveal delay={0.15}>
            <div className="mt-16">
                <LogoMarquee />
            </div>
        </Reveal>
    </section>
);

const ContactSection = () => (
    <section className="py-28 md:py-40 border-t border-white/10" data-testid="home-contact">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
                <SectionHeading
                    overline="Business Enquiries"
                    title="Let's Build the Future Together."
                    description="Tell us about your manufacturing requirement — our team will respond with capability, capacity and a plan."
                />
                <Reveal delay={0.2}>
                    <div className="mt-12 space-y-4 font-mono text-xs tracking-[0.2em] uppercase text-white/50">
                        <p data-testid="contact-turnaround">Response within 1 business day</p>
                        <p data-testid="contact-iso">ISO 9001:2015 Certified Facility</p>
                        <p>Mass manufacturing programs only</p>
                    </div>
                </Reveal>
            </div>
            <div className="lg:col-span-7">
                <Reveal delay={0.25}>
                    <EnquiryForm />
                </Reveal>
            </div>
        </div>
    </section>
);

export default function Home() {
    useEffect(() => {
        setSEO(
            "Super Finishers — Precision Tubular Components | Success Through Quality",
            "ISO 9001:2015 certified manufacturer of precision tubular components, automotive components and cycle parts. Since 2007."
        );
    }, []);

    return (
        <>
            <Hero />
            <EditorialMarquee />
            <Intro />
            <Pillars />
            <Journey />
            <CapabilitiesPreview />
            <QualityBanner />
            <InfrastructurePreview />
            <Founder />
            <Clients />
            <ContactSection />
        </>
    );
}
